import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Loader2, RefreshCw, ClipboardList, ArrowLeft, MessageSquare, Send, FileText,
  AlertTriangle, Lock, UserPlus, CheckCircle2, ArrowUpRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUSES = ['open', 'in_progress', 'blocked', 'completed', 'cancelled'];
const PRIORITY_LABEL = { 1: 'P1', 2: 'P2', 3: 'P3', 4: 'P4' };

export function ServiceRequestsInboxPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      q.set('scope', 'org');
      if (statusFilter) q.set('status', statusFilter);
      const r = await axios.get(`${API_URL}/api/service-requests?${q.toString()}`, { headers: authHeader() });
      setRequests(r.data?.requests || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load requests');
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="service-requests-inbox">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342] flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#0E7490]" /> Service Requests
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Inbox for requests submitted by service providers in your organization.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/service-request-types" className="text-xs text-[#0E7490] hover:underline">Manage types →</Link>
          <Button size="sm" variant="outline" onClick={load} data-testid="inbox-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-1 text-xs">
        <button
          onClick={() => setStatusFilter('')}
          className={`px-2.5 py-1 rounded-full border ${!statusFilter ? 'bg-[#0E7490] text-white border-[#0E7490]' : 'bg-white text-[#64748B] border-[#E2E8F0] hover:bg-[#F8FAFC]'}`}
          data-testid="filter-all"
        >All</button>
        {STATUSES.map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-2.5 py-1 rounded-full border ${statusFilter === s ? 'bg-[#0E7490] text-white border-[#0E7490]' : 'bg-white text-[#64748B] border-[#E2E8F0] hover:bg-[#F8FAFC]'}`}
            data-testid={`filter-${s}`}
          >{s.replace('_', ' ')}</button>
        ))}
      </div>

      {loading ? (
        <div className="p-12 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>
      ) : requests.length === 0 ? (
        <Card className="p-12 text-center text-sm text-[#94A3B8] border-[#E2E8F0]" data-testid="no-requests">
          No requests match this filter.
        </Card>
      ) : (
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0] text-left text-[#64748B] text-xs">
                <tr>
                  <th className="px-3 py-2 font-medium">Title</th>
                  <th className="px-3 py-2 font-medium">Form / Type</th>
                  <th className="px-3 py-2 font-medium">Patient</th>
                  <th className="px-3 py-2 font-medium">Requester</th>
                  <th className="px-3 py-2 font-medium">Priority</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC] cursor-pointer" data-testid={`inbox-row-${r.id}`}>
                    <td className="px-3 py-2"><Link to={`/admin/service-requests/${r.id}`} className="text-[#0A2342] font-medium hover:text-[#0E7490] hover:underline">{r.title}</Link></td>
                    <td className="px-3 py-2 text-[#64748B]">{r.type_name || r.form_name || '—'}</td>
                    <td className="px-3 py-2 text-[#64748B]">{r.patient_name || (r.requested_new_patient ? <span className="text-amber-700">New requested</span> : '—')}</td>
                    <td className="px-3 py-2 text-[#64748B]">{r.requester_name || '—'}</td>
                    <td className="px-3 py-2"><PriorityBadge p={r.priority} /></td>
                    <td className="px-3 py-2"><StatusBadge s={r.status} /></td>
                    <td className="px-3 py-2 text-[#64748B] whitespace-nowrap">{(r.created_at || '').slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function ServiceRequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [commentBody, setCommentBody] = useState('');
  const [commentInternal, setCommentInternal] = useState(false);
  const [me, setMe] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, meRes] = await Promise.all([
        axios.get(`${API_URL}/api/service-requests/${id}`, { headers: authHeader() }),
        axios.get(`${API_URL}/api/auth/me`, { headers: authHeader() }).catch(() => ({ data: null })),
      ]);
      setReq(reqRes.data);
      setMe(meRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load request');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const patch = async (updates) => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API_URL}/api/service-requests/${id}`, updates, { headers: authHeader() });
      setReq(prev => ({ ...prev, ...r.data, comments: prev.comments, activity: prev.activity, form_submission: prev.form_submission }));
      toast.success('Updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const postComment = async () => {
    if (!commentBody.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/service-requests/${id}/comments`,
        { body: commentBody, is_internal: commentInternal },
        { headers: authHeader() });
      setCommentBody(''); setCommentInternal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Comment failed');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-12 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>;
  if (!req) return <div className="p-12 text-center text-[#64748B]">Not found</div>;

  const isAdmin = ['super_admin', 'practice_admin', 'director_patient_services'].includes(me?.role);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="service-request-detail">
      <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-[#0E7490] hover:underline mb-3">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </button>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-4">
          <Card className="p-5 border-[#E2E8F0]">
            <h1 className="text-2xl font-bold text-[#0A2342]" data-testid="request-title">{req.title}</h1>
            <p className="text-xs text-[#94A3B8] mt-1">
              {req.type_name || req.form_name || '—'} · By {req.requester_name || '—'} · {(req.created_at || '').slice(0, 19).replace('T', ' ')}
            </p>
            {req.patient_name && (
              <p className="text-xs mt-1"><span className="text-[#64748B]">Patient:</span> <span className="text-[#0A2342] font-medium">{req.patient_name}</span></p>
            )}
            {req.description && <p className="text-sm text-[#475569] mt-3 whitespace-pre-wrap">{req.description}</p>}
          </Card>

          {req.requested_new_patient && !req.patient_id && (
            <Card className="border-amber-300 bg-amber-50" data-testid="new-patient-request-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm text-amber-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Requester asked to ADD a new patient</CardTitle>
                {isAdmin && (
                  <Button
                    size="sm"
                    onClick={async () => {
                      if (!window.confirm(`Create patient "${req.requested_new_patient?.first_name || ''} ${req.requested_new_patient?.last_name || ''}" and link to this request?`)) return;
                      setBusy(true);
                      try {
                        const r = await axios.post(`${API_URL}/api/service-requests/${id}/convert-to-patient`, {}, { headers: authHeader() });
                        toast.success('Patient created and linked');
                        if (r.data?.new_patient_id) {
                          // small breathing room for state, then go to patient
                          setTimeout(() => navigate(`/patients/${r.data.new_patient_id}`), 600);
                        }
                        load();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Convert failed');
                      } finally { setBusy(false); }
                    }}
                    disabled={busy}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                    data-testid="convert-to-patient-btn"
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UserPlus className="w-3.5 h-3.5 mr-1" />} Convert to Patient
                  </Button>
                )}
              </CardHeader>
              <CardContent className="text-xs text-[#0A2342] grid grid-cols-2 gap-2">
                {Object.entries(req.requested_new_patient || {}).map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[10px] uppercase tracking-wide text-amber-700">{k.replace(/_/g, ' ')}</div>
                    <div className="text-sm">{v || '—'}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {req.patient_id && req.patient_name && (
            <Card className="border-emerald-300 bg-emerald-50" data-testid="patient-linked-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm text-emerald-800 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Linked to patient: {req.patient_name}</CardTitle>
                <button onClick={() => navigate(`/patients/${req.patient_id}`)} className="text-xs text-emerald-800 hover:underline inline-flex items-center gap-1" data-testid="open-linked-patient">
                  Open patient <ArrowUpRight className="w-3 h-3" />
                </button>
              </CardHeader>
            </Card>
          )}

          {req.form_submission && (
            <Card className="border-[#0E7490]/30 bg-[#0E7490]/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#0E7490] flex items-center gap-2"><FileText className="w-4 h-4" /> Linked form: {req.form_submission.form_name}</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-[#0A2342]">
                <pre className="whitespace-pre-wrap font-mono bg-white border border-[#E2E8F0] p-3 rounded">{JSON.stringify(req.form_submission.data, null, 2)}</pre>
              </CardContent>
            </Card>
          )}

          <Card className="border-[#E2E8F0]">
            <div className="p-4 border-b border-[#E2E8F0] font-semibold text-[#0A2342] flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[#0E7490]" /> Conversation
              <span className="text-xs font-normal text-[#64748B]">({(req.comments || []).length})</span>
            </div>
            <div className="divide-y divide-[#F1F5F9]">
              {(req.comments || []).length === 0 && <div className="p-6 text-center text-sm text-[#94A3B8]">No comments yet.</div>}
              {(req.comments || []).map(c => (
                <div key={c.id} className={`p-4 ${c.is_internal ? 'bg-amber-50' : ''}`} data-testid={`comment-${c.id}`}>
                  <div className="flex items-center gap-2 text-xs text-[#64748B] mb-1">
                    <span className="font-medium text-[#0A2342]">{c.author_name}</span>
                    <span className="text-[10px]">{c.author_role}</span>
                    <span>·</span>
                    <span>{(c.created_at || '').slice(0, 19).replace('T', ' ')}</span>
                    {c.is_internal && <Badge className="bg-amber-200 text-amber-800 border-amber-300 text-[10px] ml-1"><Lock className="w-3 h-3 mr-0.5 inline" /> Internal</Badge>}
                  </div>
                  <p className="text-sm text-[#0A2342] whitespace-pre-wrap">{c.body}</p>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-[#E2E8F0] bg-[#F8FAFC]">
              <textarea
                rows={3} placeholder="Write a reply…"
                value={commentBody} onChange={(e) => setCommentBody(e.target.value)}
                className="w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-sm bg-white"
                data-testid="comment-body"
              />
              <div className="flex items-center justify-between mt-2">
                {isAdmin ? (
                  <label className="flex items-center gap-2 text-xs text-[#64748B]">
                    <input type="checkbox" checked={commentInternal} onChange={(e) => setCommentInternal(e.target.checked)} data-testid="comment-internal" />
                    <Lock className="w-3 h-3" /> Internal note
                  </label>
                ) : <span />}
                <Button onClick={postComment} disabled={busy || !commentBody.trim()} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="comment-submit">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Post
                </Button>
              </div>
            </div>
          </Card>

          {(req.activity || []).length > 0 && (
            <Card className="p-4 border-[#E2E8F0]">
              <div className="font-semibold text-[#0A2342] mb-3 text-sm">Activity</div>
              <ul className="space-y-1">
                {req.activity.map((a, i) => (
                  <li key={i} className="text-xs text-[#64748B] flex items-start gap-2">
                    <span className="text-[#94A3B8] mt-0.5">•</span>
                    <span>
                      <strong className="text-[#0A2342]">{a.actor_name || '—'}</strong>{' '}
                      {labelActivity(a)}{' '}
                      <span className="text-[#94A3B8]">— {(a.created_at || '').slice(0, 19).replace('T', ' ')}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        <aside className="space-y-4">
          <Card className="p-4 border-[#E2E8F0]">
            <h3 className="text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-3">Properties</h3>
            <PropField label="Status">
              <select value={req.status} onChange={(e) => patch({ status: e.target.value })} disabled={busy} className="w-full text-sm rounded-md border border-[#E2E8F0] px-2 py-1 bg-white" data-testid="prop-status">
                {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </PropField>
            <PropField label="Priority">
              <select value={req.priority || 3} onChange={(e) => patch({ priority: parseInt(e.target.value, 10) })} disabled={busy} className="w-full text-sm rounded-md border border-[#E2E8F0] px-2 py-1 bg-white" data-testid="prop-priority">
                <option value={1}>P1 — Critical</option>
                <option value={2}>P2 — High</option>
                <option value={3}>P3 — Moderate</option>
                <option value={4}>P4 — Low</option>
              </select>
            </PropField>
            <div className="mb-3">
              <div className="text-[11px] text-[#94A3B8] mb-1">Assignee</div>
              <div className="text-sm text-[#0A2342]">{req.assignee_name || <span className="text-[#94A3B8]">— Unassigned —</span>}</div>
            </div>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function PropField({ label, children }) {
  return (
    <div className="mb-3">
      <div className="text-[11px] text-[#94A3B8] mb-1">{label}</div>
      {children}
    </div>
  );
}
function PriorityBadge({ p }) {
  const map = { 1: 'bg-rose-100 text-rose-700 border-rose-200', 2: 'bg-orange-100 text-orange-700 border-orange-200', 3: 'bg-amber-100 text-amber-700 border-amber-200', 4: 'bg-slate-100 text-slate-600 border-slate-200' };
  return <Badge variant="outline" className={`${map[p] || ''} text-[10px]`}>{PRIORITY_LABEL[p] || '—'}</Badge>;
}
function StatusBadge({ s }) {
  const map = { open: 'bg-sky-50 text-sky-700 border-sky-200', in_progress: 'bg-amber-50 text-amber-700 border-amber-200', blocked: 'bg-rose-50 text-rose-700 border-rose-200', completed: 'bg-emerald-50 text-emerald-700 border-emerald-200', cancelled: 'bg-slate-50 text-slate-600 border-slate-200' };
  return <Badge variant="outline" className={`${map[s] || ''} text-[10px]`}>{(s || '').replace('_', ' ')}</Badge>;
}
function labelActivity(a) {
  switch (a.action) {
    case 'created': return <>created the request</>;
    case 'status_changed': return <>changed status <strong>{a.from}</strong> → <strong>{a.to}</strong></>;
    case 'priority_changed': return <>changed priority <strong>P{a.from}</strong> → <strong>P{a.to}</strong></>;
    case 'assigned': return <>reassigned the request</>;
    case 'patient_created': return <>created patient <strong>{a.patient_name}</strong> from this request</>;
    default: return <>{a.action}</>;
  }
}
